import './index.css';
import React, { useState } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const queryClient = new QueryClient();

interface Machine {
  index: number;
  type: 'washer' | 'dryer' | 'dynamic-dryer';
  doorState: 'closed' | 'open';
  state: 'idle' | 'running' | 'ready-to-start';
  runLength: number;
  runStartedAt?: string;
  lastSignalReceivedAt?: string;
  extraSignalsReceived?: number;
}

interface MachinesResponse {
  machines: Machine[];
}

interface DiResponse {
  slot: number;
  io: {
    di: {
      diIndex: number;
      diMode: number;
      diStatus: number;
    }[];
  };
}

interface DoResponse {
  slot: number;
  io: {
    do: {
      doIndex: number;
      doMode: number;
      doPulseStatus: number;
      doPulseCount: number;
      doPulseOnWidth: number;
      doPulseOffWidth: number;
    }[];
  };
}

interface PulseData {
  io: {
    do: {
      doIndex: number;
      doMode: number;
      doPulseStatus: number;
      doPulseCount: number;
      doPulseOnWidth: number;
      doPulseOffWidth: number;
    }[];
  };
}

const fetchMachines = async (): Promise<MachinesResponse> => {
  const response = await fetch('/machines');
  if (!response.ok) {
    throw new Error('Failed to fetch machines');
  }
  return response.json() as any;
};

const fetchDiStatus = async (): Promise<DiResponse> => {
  const response = await fetch('/api/slot/0/io/di');
  if (!response.ok) {
    throw new Error('Failed to fetch DI status');
  }
  return response.json() as any;
};

const fetchDoStatus = async (): Promise<DoResponse> => {
  const response = await fetch('/api/slot/0/io/do');
  if (!response.ok) {
    throw new Error('Failed to fetch DO status');
  }
  return response.json() as any;
};

const sendPulse = async (pulseData: PulseData): Promise<void> => {
  const response = await fetch('/api/slot/0/io/do', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(pulseData),
  });
  if (!response.ok) {
    throw new Error('Failed to send pulse');
  }
};

const switchDoor = async (machineIndex: number): Promise<void> => {
  const response = await fetch(`/machines/${machineIndex}/switch-door`, {
    method: 'PUT',
  });
  if (!response.ok) {
    throw new Error('Failed to switch door');
  }
};

const PulseControlCard: React.FC<{ machineIndex: number; machineType: string }> = ({ 
  machineIndex, 
  machineType 
}) => {
  const [pulseCount, setPulseCount] = useState(1);
  const [pulseOnWidth, setPulseOnWidth] = useState(2000);
  const [pulseOffWidth, setPulseOffWidth] = useState(2000);
  const queryClient = useQueryClient();

  const sendPulseMutation = useMutation({
    mutationFn: (pulseData: PulseData) => sendPulse(pulseData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['do-status'] });
      queryClient.invalidateQueries({ queryKey: ['di-status'] });
      queryClient.invalidateQueries({ queryKey: ['machines'] });
    },
  });

  const handleSendPulse = () => {
    const pulseData: PulseData = {
      io: {
        do: [{
          doIndex: machineIndex,
          doMode: 1,
          doPulseStatus: 1,
          doPulseCount: pulseCount,
          doPulseOnWidth: pulseOnWidth,
          doPulseOffWidth: pulseOffWidth,
        }]
      }
    };
    sendPulseMutation.mutate(pulseData);
  };

  const getMachineTypeDisplay = () => {
    switch (machineType) {
      case 'washer':
        return '🧺';
      case 'dryer':
        return '🔥';
      case 'dynamic-dryer':
        return '⚡';
      default:
        return '🔧';
    }
  };

  return (
    <div className="p-4 border border-gray-300 rounded-lg bg-blue-50">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">{getMachineTypeDisplay()}</span>
        <h4 className="font-semibold">Machine {machineIndex} Pulse Control</h4>
      </div>
      
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pulse Count
          </label>
          <input
            type="number"
            min="1"
            max="10"
            value={pulseCount}
            onChange={(e) => setPulseCount(Number(e.target.value))}
            className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            On Width (ms)
          </label>
          <input
            type="number"
            min="10"
            max="5000"
            step="10"
            value={pulseOnWidth}
            onChange={(e) => setPulseOnWidth(Number(e.target.value))}
            className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Off Width (ms)
          </label>
          <input
            type="number"
            min="10"
            max="5000"
            step="10"
            value={pulseOffWidth}
            onChange={(e) => setPulseOffWidth(Number(e.target.value))}
            className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
        
        <button
          onClick={handleSendPulse}
          disabled={sendPulseMutation.isPending}
          className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {sendPulseMutation.isPending ? 'Sending...' : 'Send Pulse'}
        </button>
        
        {sendPulseMutation.isError && (
          <p className="text-red-600 text-xs">
            Error: {sendPulseMutation.error?.message}
          </p>
        )}
        
        {sendPulseMutation.isSuccess && (
          <p className="text-green-600 text-xs">Pulse sent successfully!</p>
        )}
      </div>
    </div>
  );
};

const IOStatusPanel: React.FC = () => {
  const { data: doData, isLoading: doLoading } = useQuery({
    queryKey: ['do-status'],
    queryFn: fetchDoStatus,
    refetchInterval: 1000,
  });

  const { data: diData, isLoading: diLoading } = useQuery({
    queryKey: ['di-status'],
    queryFn: fetchDiStatus,
    refetchInterval: 1000,
  });

  if (doLoading || diLoading) {
    return (
      <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
        <h3 className="font-semibold mb-2">I/O Status</h3>
        <p className="text-sm text-gray-600">Loading...</p>
      </div>
    );
  }

  const doStatuses = doData?.io.do || [];
  const diStatuses = diData?.io.di || [];

  return (
    <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
      <h3 className="font-semibold mb-3">I/O Status Monitor</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Digital Outputs (DO)</h4>
          <div className="space-y-1">
            {doStatuses.map((doStatus) => (
              <div 
                key={doStatus.doIndex} 
                className="flex items-center justify-between text-xs p-2 bg-white rounded"
              >
                <span>DO {doStatus.doIndex}</span>
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    doStatus.doPulseStatus === 1 ? 'bg-red-500' : 'bg-gray-300'
                  }`}></span>
                  <span className="text-gray-600">
                    {doStatus.doPulseCount > 0 && `Count: ${doStatus.doPulseCount}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Digital Inputs (DI)</h4>
          <div className="space-y-1">
            {diStatuses.map((diStatus) => (
              <div 
                key={diStatus.diIndex} 
                className="flex items-center justify-between text-xs p-2 bg-white rounded"
              >
                <span>DI {diStatus.diIndex}</span>
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    diStatus.diStatus === 1 ? 'bg-green-500' : 'bg-gray-300'
                  }`}></span>
                  <span className="text-gray-600">
                    {diStatus.diStatus === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MachineCard: React.FC<{ machine: Machine }> = ({ 
  machine, 
}) => {
  const queryClient = useQueryClient();
  const isRunning = machine.state === 'running';
  const isReadyToStart = machine.state === 'ready-to-start';

  const switchDoorMutation = useMutation({
    mutationFn: () => switchDoor(machine.index),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines'] });
    },
  });

  const getTimeRemaining = () => {
    if (!machine.runStartedAt || machine.state !== 'running') {
      return null;
    }

    const startTime = new Date(machine.runStartedAt);
    const now = new Date();
    const elapsedSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
    const remainingSeconds = machine.runLength - elapsedSeconds;

    if (remainingSeconds <= 0) {
      return '0:00';
    }

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getMachineTypeDisplay = () => {
    switch (machine.type) {
      case 'washer':
        return '🧺 Washer';
      case 'dryer':
        return '🔥 Dryer';
      case 'dynamic-dryer':
        return '⚡ Dynamic Dryer';
      default:
        return machine.type;
    }
  };

  const getStatusColor = () => {
    if (isRunning) {
      return 'bg-green-100 border-green-300';
    }
    if (isReadyToStart) {
      return 'bg-yellow-100 border-yellow-300 animate-pulse';
    }
    return 'bg-gray-100 border-gray-300';
  };

  const getDoorStatusColor = () => {
    return machine.doorState === 'open' ? 'text-orange-600' : 'text-green-600';
  };

  const getStatusDisplayText = () => {
    if (isRunning) return 'Running';
    if (isReadyToStart) return 'Ready to Start';
    return 'Idle';
  };

  const getStatusIndicatorColor = () => {
    if (isRunning) return 'bg-green-500';
    if (isReadyToStart) return 'bg-yellow-500 animate-pulse';
    return 'bg-gray-400';
  };

  return (
    <div className={`p-4 border-2 rounded-lg transition-all duration-300 ${getStatusColor()}`}>
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-lg font-semibold">
            Machine {machine.index}
          </h3>
          <p className="text-sm text-gray-600">{getMachineTypeDisplay()}</p>
        </div>
        <div className="text-right">
          <div className={`font-medium ${getDoorStatusColor()}`}>
            🚪 Door: {machine.doorState}
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-block w-3 h-3 rounded-full ${getStatusIndicatorColor()}`}></span>
          <span className={`font-medium ${isReadyToStart ? 'text-yellow-700' : ''}`}>
            {getStatusDisplayText()}
          </span>
        </div>
        
        {isRunning && (
          <div className="text-sm text-gray-700">
            Time remaining: <span className="font-mono font-semibold">
              {getTimeRemaining()}
            </span>
          </div>
        )}
        
        
        <div className="text-xs text-gray-500 mt-1">
          Run length: {Math.floor(machine.runLength / 60)}:{(machine.runLength % 60).toString().padStart(2, '0')}
        </div>
      </div>

      <button
        onClick={() => switchDoorMutation.mutate()}
        disabled={switchDoorMutation.isPending}
        className="w-full px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
      >
        {switchDoorMutation.isPending ? 'Switching...' : 'Toggle Door'}
      </button>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [showControls, setShowControls] = useState(false);
  
  const { data: machinesData, isLoading: machinesLoading, error: machinesError } = useQuery({
    queryKey: ['machines'],
    queryFn: fetchMachines,
    refetchInterval: 1000,
  });

  const { data: diData, isLoading: diLoading, error: diError } = useQuery({
    queryKey: ['di-status'],
    queryFn: fetchDiStatus,
    refetchInterval: 1000,
  });

  if (machinesLoading || diLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading machines...</p>
        </div>
      </div>
    );
  }

  if (machinesError || diError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="text-xl font-semibold mb-2">Error loading data</p>
          <p className="text-sm">
            {machinesError?.message || diError?.message || 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  const machines = machinesData?.machines || [];
  const diStatuses = diData?.io.di || [];

  const runningStatusMap = diStatuses.reduce((acc, di) => {
    acc[di.diIndex] = di.diStatus === 1;
    return acc;
  }, {} as Record<number, boolean>);

  const runningCount = Object.values(runningStatusMap).filter(Boolean).length;
  const readyToStartCount = machines.filter(m => m.state === 'ready-to-start').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <header className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Laundry Management System
              </h1>
              <div className="flex gap-4 text-sm text-gray-600">
                <span>Total machines: {machines.length}</span>
                <span>Currently running: {runningCount}</span>
                <span className={readyToStartCount > 0 ? 'text-yellow-600 font-semibold animate-pulse' : ''}>
                  Ready to start: {readyToStartCount}
                </span>
                <span>Available: {machines.length - runningCount - readyToStartCount}</span>
              </div>
            </div>
            <button
              onClick={() => setShowControls(!showControls)}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 font-medium"
            >
              {showControls ? 'Hide Controls' : 'Show Controls'}
            </button>
          </div>
        </header>

        {showControls && (
          <div className="mb-8 space-y-6">
            <IOStatusPanel />
            
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Pulse Controls</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {machines.map((machine) => (
                  <PulseControlCard
                    key={machine.index}
                    machineIndex={machine.index}
                    machineType={machine.type}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Machine Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {machines.map((machine) => (
              <MachineCard
                key={machine.index}
                machine={machine}
              />
            ))}
          </div>
        </div>

        {machines.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No machines found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Dashboard />
    </QueryClientProvider>
  );
};
